import { useEffect, useMemo, useCallback, useState, useRef, ReactElement } from "react";
import { auth } from "../firebase";
import { Transaction, Account, Currency, WorkspaceSettings, AccountGroup, TransactionCategory, TransactionCategoryGroup } from "../types";
import { MouseEvent } from "react";
import { useAnalyticsTransactions, TransactionWithDate } from "./Analytics/hooks/useAnalyticsTransactions";
import { useAnalyticsAccounts } from "./Analytics/hooks/useAnalyticsAccounts";
import { useAnalyticsWorkspace } from "./Analytics/hooks/useAnalyticsWorkspace";
import { useAnalyticsCategories } from "./Analytics/hooks/useAnalyticsCategories";
import { useAnalyticsCurrencies } from "./Analytics/hooks/useAnalyticsCurrencies";
import { useAnalyticsWorkspaceMembers, WorkspaceMember } from "./Analytics/hooks/useAnalyticsWorkspaceMembers";
import { useAnalyticsUIState } from "./Analytics/hooks/useAnalyticsUIState";
import { useAnalyticsHelpers, getCategoryColor } from "./Analytics/hooks/useAnalyticsHelpers";
import { useAnalyticsHandlers } from "./Analytics/hooks/useAnalyticsHandlers";
import { PeriodSelector } from "./Analytics/components/PeriodSelector";
import { AccountFilterPopover } from "./Analytics/components/AccountFilterPopover";
import { UserFilterPopover } from "./Analytics/components/UserFilterPopover";
import { AnalyticsHeader } from "./Analytics/components/AnalyticsHeader";
import { AnalyticsPieChart, CategoryData } from "./Analytics/components/AnalyticsPieChart";
import { AnalyticsCategoryList } from "./Analytics/components/AnalyticsCategoryList";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Link,
  Alert,
  Popover,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Divider,
  Tooltip as MuiTooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface AnalyticsProps {
  workspaceId: string;
  onNavigateToActivity?: (filters?: {
    category?: string;
    account?: string;
    users?: string[];
    startDate?: string;
    endDate?: string;
    typeFilter?: string;
  }) => void;
}

function Analytics({ workspaceId, onNavigateToActivity }: AnalyticsProps): ReactElement {
  // Log when Analytics component mounts/renders
  useEffect(() => {
    console.log(`[Analytics] 🎯 Component mounted/rendered`, {
      workspaceId,
      timestamp: new Date().toISOString(),
      pathname: window.location.pathname
    });
    
    return () => {
      console.log(`[Analytics] 🔄 Component unmounting`, {
        workspaceId,
        timestamp: new Date().toISOString()
      });
    };
  }, [workspaceId]);
  
  // Check if this is dev workspace
  const isDevWorkspace = workspaceId === "90Yq3rWMUo50j8kXOzr7" || workspaceId?.includes("Dev");
  
  // Data fetching hooks
  const { transactions, loading: transactionsLoading, setTransactions } = useAnalyticsTransactions({ workspaceId });
  const { accounts, setAccounts } = useAnalyticsAccounts({ workspaceId });
  const { workspaceCreatedAt, defaultCurrency, defaultCurrencyLoaded, setWorkspaceCreatedAt, setDefaultCurrency } = useAnalyticsWorkspace({ workspaceId });
  const { categories, groups, loading: categoriesLoading, setCategories, setGroups } = useAnalyticsCategories({ workspaceId });
  const { currencies, setCurrencies } = useAnalyticsCurrencies({ workspaceId, defaultCurrency });
  const { workspaceMembers, membersLoaded, selectedUsers, setWorkspaceMembers, setMembersLoaded, setSelectedUsers } = useAnalyticsWorkspaceMembers({ workspaceId });

  // Track previous defaultCurrency to detect changes
  const previousDefaultCurrencyRef = useRef<string>(defaultCurrency);
  const currencyChangeTimestampRef = useRef<number>(0);
  // Track if we've initialized the date range for current workspace
  const hasInitializedRef = useRef<boolean>(false);
  
  // Detect when defaultCurrency changes
  useEffect(() => {
    if (previousDefaultCurrencyRef.current !== defaultCurrency) {
      console.log('[Analytics] Default currency changed', {
        from: previousDefaultCurrencyRef.current,
        to: defaultCurrency,
        timestamp: new Date().toISOString()
      });
      previousDefaultCurrencyRef.current = defaultCurrency;
      // Mark when currency changed - currencies will be refetched
      currencyChangeTimestampRef.current = Date.now();
    }
  }, [defaultCurrency]);
  
  // Check if we have cached data to initialize loading state correctly
  // If transactions are already loaded (from cache), we shouldn't show loading spinner
  const hasCachedTransactions = transactions.length > 0 && !transactionsLoading;

  // UI state - initialize loading based on whether we have cached data
  const {
    loading,
    setLoading,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    selectedAccounts,
    setSelectedAccounts,
    accountFilterAnchor,
    setAccountFilterAnchor,
    selectedPeriod,
    setSelectedPeriod,
    currentPeriodOffset,
    setCurrentPeriodOffset,
    selectedTab,
    setSelectedTab,
    aggregationMode,
    setAggregationMode,
    userFilterAnchor,
    setUserFilterAnchor,
    shouldAnimate,
    setShouldAnimate,
    readyToShowChart,
    setReadyToShowChart,
    previousDataHashRef,
    previousTabRef,
    hasAnimatedOnMountRef,
    isAnimatingTabChangeRef,
  } = useAnalyticsUIState({ initialLoading: !hasCachedTransactions });

  // Track previous aggregation mode to detect changes
  const previousAggregationModeRef = useRef<"category" | "group">(aggregationMode);
  
  // Animation timer ref (same as mobile)
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Helper functions
  const {
    getCurrencySymbol,
    formatAmount,
    formatCurrency,
    getCurrencyRate,
    convertToDefaultCurrency,
    getAccountName,
    getAccountDisplayName,
    setDateRangeByPeriod: setDateRangeByPeriodHelper,
    getUserName,
  } = useAnalyticsHelpers({
    currencies,
    accounts,
    workspaceMembers,
    defaultCurrency,
    workspaceCreatedAt,
    transactions,
  });

  // Wrapper for setDateRangeByPeriod that uses state setters
  const setDateRangeByPeriod = (periodType: string, offset: number = 0): void => {
    setDateRangeByPeriodHelper(periodType, offset, setFromDate, setToDate);
  };

  // Event handlers
  const {
    handlePeriodChange,
    handlePeriodNavigation,
    handleUserFilterClick,
    handleUserFilterClose,
    handleUserToggle,
    handleSelectAllUsers,
    handleAccountFilterClick,
    handleAccountFilterClose,
    handleAccountToggle,
    handleSelectAllAccounts,
    hasActiveFilters,
    clearAllFilters,
  } = useAnalyticsHandlers({
    setSelectedPeriod,
    setCurrentPeriodOffset,
    setSelectedUsers,
    setSelectedAccounts,
    setUserFilterAnchor,
    setAccountFilterAnchor,
    setFromDate,
    setToDate,
    selectedPeriod,
    currentPeriodOffset,
    selectedUsers,
    selectedAccounts,
    workspaceMembers,
    accounts,
    fromDate,
    toDate,
    setDateRangeByPeriod,
    transactions,
  });

  // Update loading state based on transactions loading
  useEffect(() => {
    setLoading(transactionsLoading);
  }, [transactionsLoading, setLoading]);


  // Debug: Track selectedUsers changes (moved after selectedUsersSet declaration)







  // Set default Monthly period on mount - ensure period and date range are set for current month
  // Only initialize once when component mounts (workspaceId changes)
  useEffect(() => {
    // Reset initialization flag when workspace changes
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      
      // Set period to Monthly if not already set
      if (!selectedPeriod || selectedPeriod === "") {
        setSelectedPeriod("Monthly");
      }
      
      // Always set current month date range on initial mount
      // This ensures we show current month by default when entering Analytics
      const today = new Date();
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      
      // Use local date strings to avoid timezone issues
      const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
      const toStr = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}`;
      
      setFromDate(fromStr);
      setToDate(toStr);
    }
  }, [workspaceId]); // Only depend on workspaceId to initialize once per workspace
  
  // Reset initialization flag when workspace changes
  useEffect(() => {
    hasInitializedRef.current = false;
  }, [workspaceId]);

  // Update date range when workspace creation date loads and "All time" is selected
  useEffect(() => {
    if (selectedPeriod === "All time" && workspaceCreatedAt) {
      setDateRangeByPeriod("All time", 0);
    }
  }, [workspaceCreatedAt, selectedPeriod]);

  // Note: User filter now works like account filter - simple selection, no transaction checks

  // Debug: Track userFilterAnchor changes
  useEffect(() => {
    console.log("[Analytics] userFilterAnchor changed", { 
      hasAnchorEl: !!userFilterAnchor,
      anchorEl: userFilterAnchor,
      anchorElTag: userFilterAnchor?.tagName,
      anchorElId: userFilterAnchor?.id,
      anchorElClassName: userFilterAnchor?.className
    });
  }, [userFilterAnchor]);


  // Filter transactions based on selected tab
  // Use Set for O(1) user and account lookup performance instead of O(n) array.includes()
  const selectedUsersSet = useMemo(() => new Set(selectedUsers), [selectedUsers]);
  const selectedAccountsSet = useMemo(() => new Set(selectedAccounts), [selectedAccounts]);

  
  const filteredTransactions = useMemo(() => {
    let filteredByType = 0;
    let filteredByDate = 0;
    let filteredByAccount = 0;
    let filteredByUser = 0;
    let passedAll = 0;
    
    const result = transactions.filter((transaction) => {
      // Filter by selected tab type
      if (selectedTab === "expenses" && transaction.type !== "expense") {
        filteredByType++;
        return false;
      }
      if (selectedTab === "incomes" && transaction.type !== "income") {
        filteredByType++;
        return false;
      }
      if (selectedTab === "adjustments" && transaction.type !== "adjustment") {
        filteredByType++;
        return false;
      }

      // Date range filter - use string comparison (same as mobile) to avoid timezone issues
      if (fromDate || toDate) {
        const transactionDate = transaction.date;
        if (!transactionDate) {
          filteredByDate++;
          return false;
        }
        const transactionDateStr = `${transactionDate.getFullYear()}-${String(transactionDate.getMonth() + 1).padStart(2, "0")}-${String(transactionDate.getDate()).padStart(2, "0")}`;
        if (fromDate && transactionDateStr < fromDate) {
          filteredByDate++;
          return false;
        }
        if (toDate && transactionDateStr > toDate) {
          filteredByDate++;
          return false;
        }
      }

      // Account filter (expenses, incomes, and adjustments can have accountId)
      // If selectedAccounts is empty, show all transactions (no filtering)
      // If selectedAccounts contains accounts, only show those accounts' transactions
      if (selectedAccounts.length > 0 && transaction.accountId) {
        if (!selectedAccountsSet.has(transaction.accountId)) {
          filteredByAccount++;
          return false;
        }
      }

      // User filter - use Set for O(1) lookup (much faster than array.includes())
      // Only filter if selectedUsers has values AND transaction has a userId
      // If selectedUsers is empty, show all transactions (no filtering)
      if (selectedUsers.length > 0 && transaction.userId) {
        if (!selectedUsersSet.has(transaction.userId)) {
          filteredByUser++;
          return false;
        }
      }

      passedAll++;
      return true;
    });
    
    // Get sample transaction dates for debugging
    const sampleTransactionDates = transactions.slice(0, 10).map(t => ({
      id: t.id,
      date: t.date?.toISOString(),
      dateLocal: t.date?.toLocaleString(),
      type: t.type,
      category: t.category,
      accountId: t.accountId,
      userId: t.userId,
    }));
    
    // Get all transaction types breakdown
    const allTransactionTypes = transactions.reduce((acc, t) => {
      acc[t.type] = (acc[t.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    // Get filtered transaction types breakdown
    const filteredTransactionTypes = result.reduce((acc, t) => {
      acc[t.type] = (acc[t.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    
    return result;
  }, [transactions, fromDate, toDate, selectedAccounts.length, selectedAccountsSet, selectedTab, selectedUsers.length, selectedUsersSet, workspaceId, isDevWorkspace]);

  // Create mapping from category name to group name
  // Only build map when categories and groups are loaded (not while loading)
  const categoryToGroupMap = useMemo(() => {
    // Don't build map if still loading - return empty map
    if (categoriesLoading) {
      return new Map<string, string>();
    }
    
    // If no categories or groups, return empty map
    if (categories.length === 0 || groups.length === 0) {
      return new Map<string, string>();
    }
    
    console.log('[Analytics] Building categoryToGroupMap', {
      categoriesCount: categories.length,
      groupsCount: groups.length,
      categoriesLoading,
      timestamp: new Date().toISOString()
    });
    
    const map = new Map<string, string>();
    categories.forEach((cat) => {
      if (cat.groupId) {
        const group = groups.find((g) => g.id === cat.groupId);
        if (group) {
          map.set(cat.name, group.name);
        }
      }
    });
    
    console.log('[Analytics] categoryToGroupMap built', {
      mapSize: map.size,
      sampleMappings: Array.from(map.entries()).slice(0, 5)
    });
    
    return map;
  }, [categories, groups, categoriesLoading]);

  // Group transactions by category (expenses/incomes) or account (adjustments)
  // When aggregationMode is "group", group by group name instead of category name
  const categoryData = useMemo((): CategoryData[] => {
    console.log('[Analytics] Computing categoryData', {
      filteredTransactionsCount: filteredTransactions.length,
      aggregationMode,
      selectedTab,
      categoriesLoading,
      categoriesCount: categories.length,
      groupsCount: groups.length,
      categoryToGroupMapSize: categoryToGroupMap.size,
      defaultCurrencyLoaded,
      defaultCurrency,
      currenciesCount: currencies.length,
      hasDefaultCurrency: currencies.some(c => c.code === defaultCurrency),
      timestamp: new Date().toISOString()
    });
    
    // CRITICAL: Wait for default currency to be fully loaded before calculating
    // This prevents showing values in wrong currency (USD -> AMD flicker)
    // Don't calculate if defaultCurrency is empty (not loaded yet) or not loaded flag is false
    if (!defaultCurrencyLoaded || !defaultCurrency || defaultCurrency === "") {
      console.log('[Analytics] Default currency not loaded yet, returning empty array', {
        defaultCurrencyLoaded,
        defaultCurrency,
        currenciesCount: currencies.length
      });
      return [];
    }
    
    // Wait for currencies to be loaded before calculating
    // This prevents showing intermediate values with wrong currency conversions
    // Also ensure default currency exists in currencies array (or currencies array is ready)
    const hasDefaultCurrencyInList = currencies.some(c => c.code === defaultCurrency);
    if (currencies.length === 0) {
      console.log('[Analytics] Currencies array empty, returning empty array', {
        defaultCurrencyLoaded,
        currenciesCount: currencies.length,
        defaultCurrency
      });
      return [];
    }
    
    // Additional check: if we have currencies but default currency is not in the list,
    // it might mean currencies are still loading for the new default currency
    // Also check if defaultCurrency recently changed (within last 500ms) - wait for refetch
    const currencyRecentlyChanged = Date.now() - currencyChangeTimestampRef.current < 500;
    if (!hasDefaultCurrencyInList && (currencies.length > 0 || currencyRecentlyChanged)) {
      console.log('[Analytics] Default currency not found in currencies list or recently changed, waiting...', {
        defaultCurrency,
        availableCurrencies: currencies.map(c => c.code),
        currenciesCount: currencies.length,
        currencyRecentlyChanged,
        timeSinceChange: Date.now() - currencyChangeTimestampRef.current
      });
      return [];
    }
    
    // If switching to group mode, ensure categories and groups are fully loaded before calculating
    // Return empty array if data isn't ready to prevent showing intermediate states with wrong values
    if (aggregationMode === "group" && selectedTab !== "adjustments") {
      if (categoriesLoading || categories.length === 0 || groups.length === 0 || categoryToGroupMap.size === 0) {
        console.log('[Analytics] Group mode selected but data not ready, returning empty array', {
          categoriesLoading,
          categoriesLength: categories.length,
          groupsLength: groups.length,
          mapSize: categoryToGroupMap.size
        });
        return [];
      }
    }
    
    const categoryMap = new Map<string, number>();

    filteredTransactions.forEach((transaction) => {
      let category: string;
      if (transaction.type === "adjustment") {
        // For adjustments, group by account name (use stored accountName for deleted accounts)
        category = getAccountName(transaction.accountId, transaction.accountName) || "Unknown Account";
      } else {
        // For expenses and incomes, group by category or group based on aggregationMode
        const transactionCategory = transaction.category || "Other";
        
        if (aggregationMode === "group" && selectedTab !== "adjustments") {
          // Map category name to group name
          // At this point, we know data is ready (checked above)
          const groupName = categoryToGroupMap.get(transactionCategory);
          // Always use category name if no group found, never use "Uncategorized"
          if (!groupName) {
            console.log('[Analytics] No group found for category, using category name', {
              transactionCategory,
              availableCategories: Array.from(categoryToGroupMap.keys()).slice(0, 5)
            });
          }
          category = groupName || transactionCategory;
        } else {
          category = transactionCategory;
        }
      }
      
      const originalAmount = transaction.amount || 0;
      // Convert amount to default currency before summing
      // Prefer using amountInDefaultCurrency if available (stored at transaction time)
      // For adjustments, use actual value (not absolute) to calculate net per account
      const amountToSum = transaction.type === "adjustment" 
        ? originalAmount  // Use actual value for net calculation
        : originalAmount;
      const convertedAmount = convertToDefaultCurrency(
        amountToSum, 
        transaction.currency,
        transaction.amountInDefaultCurrency,
        transaction.defaultCurrencyAtTime
      );
      categoryMap.set(category, (categoryMap.get(category) || 0) + convertedAmount);
    });
    
    const categoryNames = Array.from(categoryMap.keys());
    console.log('[Analytics] categoryData computed', {
      uniqueCategories: categoryNames,
      hasUncategorized: categoryNames.includes('Uncategorized'),
      categoryCount: categoryNames.length
    });

    // First, create array and sort by absolute value (highest to lowest)
    const sortedData = Array.from(categoryMap.entries())
      .map(([name, value]) => ({
        name,
        value: Math.round(value * 100) / 100,
      }))
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

    // Calculate total for ratio calculation (use absolute values for visualization)
    const total = sortedData.reduce((sum, item) => sum + Math.abs(item.value), 0);

    // Assign colors - use consistent, distinct colors for each category for better visibility
    // Each category gets a unique color from the palette, making it easy to distinguish
    const data: CategoryData[] = sortedData.map((item, index) => {
      return {
        name: item.name,
        value: item.value,
        color: getCategoryColor(index),
      };
    });

    return data;
  }, [filteredTransactions, currencies, defaultCurrency, defaultCurrencyLoaded, accounts, aggregationMode, categoryToGroupMap, selectedTab, categories.length, groups.length]);


  // Calculate amounts for expenses and incomes tabs (filtered by date/accounts/users but not by tab)
  const expensesAndIncomesAmounts = useMemo(() => {
    // CRITICAL: Wait for default currency to be fully loaded before calculating
    if (!defaultCurrencyLoaded || !defaultCurrency || defaultCurrency === "") {
      return { expensesAmount: 0, incomesAmount: 0 };
    }
    
    const hasDefaultCurrencyInList = currencies.some(c => c.code === defaultCurrency);
    if (currencies.length === 0) {
      return { expensesAmount: 0, incomesAmount: 0 };
    }
    
    const currencyRecentlyChanged = Date.now() - currencyChangeTimestampRef.current < 500;
    if (!hasDefaultCurrencyInList && (currencies.length > 0 || currencyRecentlyChanged)) {
      return { expensesAmount: 0, incomesAmount: 0 };
    }

    const selectedUsersSet = new Set(selectedUsers);
    const selectedAccountsSet = new Set(selectedAccounts);
    
    const baseFiltered = transactions.filter((transaction) => {
      // Date range filter
      if (fromDate || toDate) {
        const transactionDate = transaction.date;
        if (!transactionDate) return false;
        const transactionDateStr = `${transactionDate.getFullYear()}-${String(transactionDate.getMonth() + 1).padStart(2, '0')}-${String(transactionDate.getDate()).padStart(2, '0')}`;
        if (fromDate && transactionDateStr < fromDate) return false;
        if (toDate && transactionDateStr > toDate) return false;
      }
      // Account filter
      if (selectedAccounts.length > 0 && transaction.accountId) {
        if (!selectedAccountsSet.has(transaction.accountId)) return false;
      }
      // User filter
      if (selectedUsers.length > 0 && transaction.userId) {
        if (!selectedUsersSet.has(transaction.userId)) return false;
      }
      return true;
    });

    const expensesAmount = baseFiltered
      .filter(t => t.type === "expense")
      .reduce((sum, t) => sum + convertToDefaultCurrency(
        Math.abs(t.amount || 0),
        t.currency,
        t.amountInDefaultCurrency,
        t.defaultCurrencyAtTime
      ), 0);
    
    const incomesAmount = baseFiltered
      .filter(t => t.type === "income")
      .reduce((sum, t) => sum + convertToDefaultCurrency(
        Math.abs(t.amount || 0),
        t.currency,
        t.amountInDefaultCurrency,
        t.defaultCurrencyAtTime
      ), 0);

    return { expensesAmount, incomesAmount };
  }, [transactions, fromDate, toDate, selectedAccounts, selectedUsers, convertToDefaultCurrency, defaultCurrencyLoaded, defaultCurrency, currencies]);

  const totalAmount = useMemo(() => {
    // CRITICAL: Wait for default currency to be fully loaded before calculating
    // This prevents showing values in wrong currency (USD -> AMD flicker)
    // Don't calculate if defaultCurrency is empty (not loaded yet) or not loaded flag is false
    if (!defaultCurrencyLoaded || !defaultCurrency || defaultCurrency === "") {
      console.log('[Analytics] totalAmount: Default currency not loaded yet', {
        defaultCurrencyLoaded,
        defaultCurrency,
        currenciesCount: currencies.length
      });
      return 0;
    }
    
    // Wait for currencies to be loaded before calculating
    // This prevents showing intermediate values with wrong currency conversions
    const hasDefaultCurrencyInList = currencies.some(c => c.code === defaultCurrency);
    if (currencies.length === 0) {
      console.log('[Analytics] totalAmount: Currencies array empty', {
        defaultCurrencyLoaded,
        currenciesCount: currencies.length,
        defaultCurrency
      });
      return 0;
    }
    
    // Additional check: if we have currencies but default currency is not in the list,
    // it might mean currencies are still loading for the new default currency
    // Also check if defaultCurrency recently changed (within last 500ms) - wait for refetch
    const currencyRecentlyChanged = Date.now() - currencyChangeTimestampRef.current < 500;
    if (!hasDefaultCurrencyInList && (currencies.length > 0 || currencyRecentlyChanged)) {
      console.log('[Analytics] totalAmount: Default currency not found in currencies list or recently changed', {
        defaultCurrency,
        availableCurrencies: currencies.map(c => c.code),
        currencyRecentlyChanged,
        timeSinceChange: Date.now() - currencyChangeTimestampRef.current
      });
      return 0;
    }
    
    if (selectedTab === "adjustments") {
      // For adjustments, calculate net total (sum of all adjustments, can be negative)
      return filteredTransactions.reduce((sum, t) => {
        const originalAmount = t.amount || 0;
        // Prefer using amountInDefaultCurrency if available (stored at transaction time)
        const convertedAmount = convertToDefaultCurrency(
          originalAmount, 
          t.currency,
          t.amountInDefaultCurrency,
          t.defaultCurrencyAtTime
        );
        return sum + convertedAmount;
      }, 0);
    } else {
      // For expenses and incomes, use absolute value
      return filteredTransactions.reduce((sum, t) => {
        const originalAmount = t.amount || 0;
        // Prefer using amountInDefaultCurrency if available (stored at transaction time)
        const convertedAmount = convertToDefaultCurrency(
          originalAmount, 
          t.currency,
          t.amountInDefaultCurrency,
          t.defaultCurrencyAtTime
        );
        return sum + convertedAmount;
      }, 0);
    }
  }, [filteredTransactions, currencies, defaultCurrency, defaultCurrencyLoaded, selectedTab, convertToDefaultCurrency]);
  
  // Debug: Log when totalAmount changes
  useEffect(() => {
    console.log('[Analytics] totalAmount changed', {
      totalAmount,
      defaultCurrency,
      defaultCurrencyLoaded,
      currenciesCount: currencies.length,
      hasDefaultCurrency: currencies.some(c => c.code === defaultCurrency),
      timestamp: new Date().toISOString()
    });
  }, [totalAmount, defaultCurrency, defaultCurrencyLoaded, currencies]);

  // Generate placeholder items based on actual user data (groups, categories, or accounts)
  const placeholderItems = useMemo((): string[] => {
    if (selectedTab === "adjustments") {
      // For adjustments, use actual account names
      return accounts
        .filter((acc) => selectedAccounts.length === 0 || selectedAccounts.includes(acc.id))
        .map((acc) => acc.accountName)
        .slice(0, 6);
    } else {
      // For expenses and incomes, use groups or categories based on aggregationMode
      const type = selectedTab === "expenses" ? "expense" : "income";
      
      if (aggregationMode === "group") {
        // Use actual group names
        return groups
          .filter((g) => g.type === type)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((g) => g.name)
          .slice(0, 8);
      } else {
        // Use actual category names
        return categories
          .filter((c) => c.type === type)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((c) => c.name)
          .slice(0, selectedTab === "expenses" ? 8 : 5);
      }
    }
  }, [selectedTab, aggregationMode, groups, categories, accounts, selectedAccounts]);

  // Memoize pie chart data to prevent animation interruptions
  const pieChartData = useMemo(() => {
    // PRIORITY: If we have real data, show it immediately (no placeholder)
    // This prevents showing placeholder when data exists
    if (categoryData.length > 0) {
      if (selectedTab === "adjustments") {
        return categoryData.map(entry => ({ ...entry, value: Math.abs(entry.value) }));
      }
      return categoryData;
    }

    // CRITICAL: When no transactions in the selected interval, always show colorful placeholder
    // (e.g. first-time login, or no transactions in date range) - never show fake 50/50 segments
    if (filteredTransactions.length === 0) {
      if (placeholderItems.length === 0) {
        return [{ name: "placeholder", value: 1, color: "#e0e0e0" }];
      }
      return placeholderItems.map((name, index) => ({
        name,
        value: 0.01, // Very small values for equal empty-state donut segments
        color: getCategoryColor(index),
      }));
    }
    
    // Only show placeholder if we have no data (categoryData empty but filteredTransactions exist - edge case)
    // Check if tab is changing (compare current tab with previous tab ref)
    const isTabChanging = previousTabRef.current !== selectedTab && hasAnimatedOnMountRef.current;
    const shouldShowPlaceholder = categoryData.length === 0 && 
      (!isTabChanging && (!hasAnimatedOnMountRef.current || isAnimatingTabChangeRef.current) && (!shouldAnimate || !readyToShowChart));
    
    if (shouldShowPlaceholder) {
      if (placeholderItems.length === 0) {
        return [{ name: "placeholder", value: 1, color: "#e0e0e0" }];
      }
      return placeholderItems.map((name, index) => ({
        name,
        value: 0.01,
        color: getCategoryColor(index),
      }));
    }
    
    // No data and not showing placeholder - use colorful placeholder (never fake 50/50)
    if (placeholderItems.length === 0) {
      return [{ name: "placeholder", value: 1, color: "#e0e0e0" }];
    }
    return placeholderItems.map((name, index) => ({
      name,
      value: 0.01,
      color: getCategoryColor(index),
    }));
  }, [categoryData, filteredTransactions.length, selectedTab, readyToShowChart, shouldAnimate, workspaceId, hasAnimatedOnMountRef, isAnimatingTabChangeRef, previousTabRef, placeholderItems, getCategoryColor]);

  // Determine if we're currently showing placeholder data (for chart rendering)
  const isShowingPlaceholder = useMemo(() => {
    // PRIORITY: If we have real data, never show placeholder
    if (categoryData.length > 0) {
      return false;
    }

    // CRITICAL: When no transactions in the selected interval, always show placeholder
    // (e.g. first-time login, or no transactions in date range) - never show fake category labels
    if (filteredTransactions.length === 0) {
      return true;
    }
    
    // Only show placeholder if we have no data
    // Check if tab is changing (compare current tab with previous tab ref)
    // This check happens synchronously during render, before the effect runs
    const isTabChanging = previousTabRef.current !== selectedTab && hasAnimatedOnMountRef.current;
    
    // Check if aggregation mode changed and group data isn't ready
    const aggregationModeChanged = previousAggregationModeRef.current !== aggregationMode;
    const groupModeNotReady = aggregationMode === "group" && selectedTab !== "adjustments" && 
      (categoriesLoading || categories.length === 0 || groups.length === 0 || categoryToGroupMap.size === 0);
    
    // Show placeholder if: 
    // 1. We have no data (categoryData.length === 0), AND
    // 2. NOT tab changing AND (We haven't animated on mount yet OR we're animating a tab change) AND animation hasn't fully started
    //    OR aggregation mode changed to group and data isn't ready yet
    return categoryData.length === 0 && (
      (!isTabChanging && ((!hasAnimatedOnMountRef.current || isAnimatingTabChangeRef.current) && (!shouldAnimate || !readyToShowChart))) || 
      (aggregationModeChanged && groupModeNotReady)
    );
  }, [categoryData.length, filteredTransactions.length, shouldAnimate, readyToShowChart, selectedTab, workspaceId, hasAnimatedOnMountRef, isAnimatingTabChangeRef, previousTabRef, aggregationMode, categoriesLoading, categories.length, groups.length, categoryToGroupMap.size]);

  // Determine if we should hide the placeholder (invisible) vs show it (visible with animation)
  // Hide placeholder when: animation hasn't started yet (waiting for animation to be ready)
  // Show placeholder/graph (visible) once readyToShowChart is true - keep it visible forever after
  const shouldHidePlaceholder = useMemo(() => {
    // Hide placeholder only if readyToShowChart is false (we haven't shown the graph yet)
    // Once readyToShowChart is true, always show the graph (never hide it again)
    // This prevents the graph from disappearing after animation completes (when shouldAnimate becomes false)
    return !readyToShowChart;
  }, [readyToShowChart]);

  // Track data changes to only animate when data actually changes
  // Include aggregationMode in hash to detect mode changes
  const dataHash = useMemo(() => {
    return `${selectedTab}-${aggregationMode}-${pieChartData.map(d => `${d.name}:${d.value}`).join('|')}`;
  }, [pieChartData, selectedTab, aggregationMode]);

  // Initialize animation state on mount (when navigating to Analytics)
  // Reset animation state on mount to ensure animations work on refresh
  useEffect(() => {
    // Reset all animation state
    previousTabRef.current = selectedTab;
    previousDataHashRef.current = "";
    hasAnimatedOnMountRef.current = false;
    setShouldAnimate(false);
    // Don't reset readyToShowChart on mount if we already have data
    // This prevents hiding the chart when navigating back to Analytics
    if (categoryData.length === 0 && pieChartData.length === 0) {
      setReadyToShowChart(false);
    } else {
      setReadyToShowChart(true);
    }
  }, []); // Empty deps - only run once on mount

  // Reset animation state when aggregation mode changes to ensure smooth transition
  useEffect(() => {
    if (previousAggregationModeRef.current !== aggregationMode) {
      previousAggregationModeRef.current = aggregationMode;
      // Reset data hash to trigger animation when mode changes
      previousDataHashRef.current = "";
    }
  }, [aggregationMode]);
  
  // Track previous categoryData to detect actual changes (same as mobile)
  const previousCategoryDataHashRef = useRef<string>("");

  // Also reset animation state when workspaceId changes (component stays mounted but data changes)
  useEffect(() => {
    // Reset animation state when workspace changes
    hasAnimatedOnMountRef.current = false;
    previousDataHashRef.current = "";
    setShouldAnimate(false);
    setReadyToShowChart(false);
    previousAggregationModeRef.current = aggregationMode;
  }, [workspaceId, isDevWorkspace, aggregationMode]);

  // Automatically assign orphaned/legacy transactions to Bypass-User for dev workspace
  // Only run in "web-mobile" mode (bypass auth enabled) to avoid reassigning real authenticated users
  // Use a ref to track if we've already run the assignment to avoid running multiple times
  const hasAutoAssignedRef = useRef(false);
  
  useEffect(() => {
    if (!isDevWorkspace) return;
    
    // Only run in bypass auth mode (web-mobile mode)
    // In web-only mode, we want to preserve real authenticated user IDs
    const { isBypassAuthEnabled } = require("../utils/bypassAuth");
    if (!isBypassAuthEnabled()) {
      return; // Skip in web-only mode
    }
    
    if (hasAutoAssignedRef.current) return; // Already ran
    
    // Run automatically when component mounts and transactions are loaded
    if (!loading && transactions.length > 0) {
      hasAutoAssignedRef.current = true; // Mark as run
      
      const assignTransactions = async () => {
        try {
          const { setupDevUser } = await import("../utils/setupDevUser");
          await setupDevUser();
        } catch (error) {
          // Silently fail - just log to console
          console.warn("[Dev Workspace] Failed to auto-assign transactions to Bypass-User:", error);
        }
      };
      
      // Run once after a short delay to avoid interfering with initial load
      const timeoutId = setTimeout(assignTransactions, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [isDevWorkspace, loading, transactions.length]);
  
  // Reset the ref when workspace changes
  useEffect(() => {
    hasAutoAssignedRef.current = false;
  }, [workspaceId]);

  // Animation trigger - only on initial mount (first time entering Analytics)
  // This works for both navigation from Activity and page refresh
  useEffect(() => {
    // Skip if still loading
    if (loading) {
      return;
    }

    // Check if initial mount - only animate on first entry (works for both navigation and refresh)
    // IMPORTANT: Check this FIRST before checking data, to prevent infinite loops
    const isInitialMount = !hasAnimatedOnMountRef.current;
    
    // If we've already animated, don't do anything (prevent infinite loops)
    if (!isInitialMount) {
      return;
    }

    // Wait for data to be ready - check if pieChartData exists (can be placeholder or real data)
    if (!pieChartData || pieChartData.length === 0) {
      return;
    }

    // Trigger animation only on initial mount - prevent infinite loops
    // Clear any existing timer
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
      animationTimerRef.current = null;
    }

    // Update refs immediately to prevent re-triggering
    previousDataHashRef.current = dataHash;
    previousTabRef.current = selectedTab;
    // Mark as animated immediately to prevent re-triggering
    hasAnimatedOnMountRef.current = true;

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Set readyToShowChart to true to show the chart
        setReadyToShowChart(true);
        
        // Set shouldAnimate directly - no delays
        setShouldAnimate(true);
        
        // Reset animation flag after animation completes (1200ms animation duration)
        animationTimerRef.current = setTimeout(() => {
          setShouldAnimate(false);
          animationTimerRef.current = null;
        }, 1800);
      });
    });

    // Cleanup function
    return () => {
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
        animationTimerRef.current = null;
      }
    };
  }, [loading]); // Only depend on loading - don't depend on pieChartData.length to prevent loops

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", bgcolor: "background.default" }}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          p: 2,
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "background.paper",
        }}
      >
        {/* Filters */}
        <Grid container spacing={2}>
          <PeriodSelector
            selectedPeriod={selectedPeriod}
            onPeriodChange={handlePeriodChange}
            fromDate={fromDate}
            toDate={toDate}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
            onPeriodNavigation={handlePeriodNavigation}
          />
          <AccountFilterPopover
            accounts={accounts}
            selectedAccounts={selectedAccounts}
            onAccountToggle={handleAccountToggle}
            onSelectAllAccounts={handleSelectAllAccounts}
            onFilterClick={handleAccountFilterClick}
            onFilterClose={handleAccountFilterClose}
                anchorEl={accountFilterAnchor}
            getAccountDisplayName={getAccountDisplayName}
          />
          <UserFilterPopover
            workspaceMembers={workspaceMembers}
            selectedUsers={selectedUsers}
            onUserToggle={handleUserToggle}
            onSelectAllUsers={handleSelectAllUsers}
            onFilterClick={handleUserFilterClick}
            onFilterClose={handleUserFilterClose}
                anchorEl={userFilterAnchor}
            getUserName={getUserName}
            transactions={transactions}
          />
          {hasActiveFilters && (
            /* @ts-expect-error - MUI Grid item prop types issue in v7 */
            <Grid item xs={12} sm={6} md={4}>
              <Button
                variant="outlined"
                size="small"
                onClick={clearAllFilters}
                startIcon={<ClearIcon />}
                sx={{
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: "action.hover",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                Clear All Filters
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: "auto", width: "100%", p: 2 }}>
        {loading ? (
          // Show chart structure with placeholder during loading
          <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                border: 1, 
                borderColor: "divider", 
                width: "100%", 
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3, position: "relative" }}>
                <Tabs
                  value={selectedTab}
                  onChange={(e, newValue) => {
                    setSelectedTab(newValue);
                  }}
                  aria-label="analytics tabs"
                  sx={{
                    "& .MuiTabs-flexContainer": {
                      justifyContent: "space-between",
                    },
                    "& .MuiTab-root:nth-of-type(1)": {
                      // Expenses - add spacing after to separate from Incomes
                      marginRight: 2,
                    },
                    "& .MuiTab-root:nth-of-type(2)": {
                      // Incomes - add gap before Adjustments
                      marginRight: "auto",
                    },
                    "& .MuiTab-root:nth-of-type(3)": {
                      // Adjustments - separate on the right
                      marginLeft: "auto",
                      borderLeft: "1px solid",
                      borderColor: "divider",
                      paddingLeft: 2,
                    },
                  }}
                >
                  <Tab label="Expenses" value="expenses" />
                  <Tab label="Incomes" value="incomes" />
                  <Tab label="Adjustments" value="adjustments" />
                </Tabs>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="h6" fontWeight="medium">
                    {selectedTab === "expenses" && "Expenses"}
                    {selectedTab === "incomes" && "Incomes"}
                    {selectedTab === "adjustments" && "Adjustments"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 3, textAlign: "right" }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {selectedTab === "expenses" && "Total Transactions"}
                      {selectedTab === "incomes" && "Total Transactions"}
                      {selectedTab === "adjustments" && "Total Adjustments"}
                    </Typography>
                    <Typography variant="h6" fontWeight="semibold" sx={{ opacity: 0.5 }}>
                      -
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {selectedTab === "expenses" && "Total Expenses"}
                      {selectedTab === "incomes" && "Total Income"}
                      {selectedTab === "adjustments" && "Total Adjustments"}
                    </Typography>
                    <Typography variant="h6" fontWeight="semibold" sx={{ opacity: 0.5 }}>
                      -
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ flex: 1, width: "100%", minHeight: 0, display: "flex", gap: 3 }}>
                {/* Pie Chart - Left Side */}
                <Box sx={{ flex: "1 1 60%", minHeight: 0, position: "relative" }}>
                  {/* Aggregation Mode Toggle - Top left of graph card, only for expenses/incomes */}
                  {selectedTab !== "adjustments" && (
                    <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 1 }}>
                      <ToggleButtonGroup
                        value={aggregationMode}
                        exclusive
                        onChange={(_, newValue) => newValue && setAggregationMode(newValue)}
                        size="small"
                        sx={{ 
                          userSelect: "none",
                          height: "24px",
                          "& .MuiToggleButton-root": {
                            px: 1,
                            py: 0.25,
                            fontSize: "0.7rem",
                            border: 1,
                            borderColor: "divider",
                            textTransform: "none",
                            minWidth: "auto",
                            "&.Mui-selected": {
                              bgcolor: "primary.main",
                              color: "primary.contrastText",
                              "&:hover": {
                                bgcolor: "primary.dark",
                              },
                            },
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          },
                        }}
                      >
                        <ToggleButton value="group" sx={{ userSelect: "none" }}>Group</ToggleButton>
                        <ToggleButton value="category" sx={{ userSelect: "none" }}>Category</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  )}
                  {/* Simple placeholder - no chart to prevent dimension recalculation */}
                  <Box sx={{ flex: "1 1 60%", minHeight: 0, border: 1, borderColor: "divider", borderRadius: 1, p: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress size={40} />
                  </Box>
                </Box>

                {/* Category List - Right Side */}
                <Box sx={{ flex: "1 1 40%", display: "flex", flexDirection: "column", borderLeft: 1, borderColor: "divider", pl: 3 }}>
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
                    {selectedTab === "expenses" && "Category Breakdown"}
                    {selectedTab === "incomes" && "Category Breakdown"}
                    {selectedTab === "adjustments" && "Account Breakdown"}
                  </Typography>
                  <Stack spacing={2} sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
                    {placeholderItems.length > 0 ? (
                      placeholderItems.map((itemName, index) => (
                        <Box key={itemName} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: "4px",
                              bgcolor: getCategoryColor(index),
                              flexShrink: 0,
                              opacity: 0.3,
                            }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: "medium",
                                color: "text.secondary",
                                textAlign: "left",
                              }}
                            >
                              {itemName}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: "right", minWidth: 60 }}>
                            <Typography variant="h6" fontWeight="semibold" color="text.secondary" sx={{ opacity: 0.5 }}>
                              -
                            </Typography>
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No data available
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Paper>
          </Box>
        ) : (
          <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                border: 1, 
                borderColor: "divider", 
                width: "100%", 
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <AnalyticsHeader
                selectedTab={selectedTab}
                onTabChange={(newValue) => {
                    setSelectedTab(newValue);
                    // Animation will be triggered by the useEffect hook
                  }}
                totalTransactions={filteredTransactions.length}
                totalAmount={totalAmount}
                formatAmount={formatAmount}
                onNavigateToActivity={onNavigateToActivity}
                selectedAccounts={selectedAccounts}
                selectedUsers={selectedUsers}
                workspaceMembersLength={workspaceMembers.length}
                fromDate={fromDate}
                toDate={toDate}
                expensesAndIncomesAmounts={expensesAndIncomesAmounts}
              />
              <Box sx={{ flex: 1, width: "100%", minHeight: 0, display: "flex", gap: 3 }}>
                {/* Pie Chart Container with Aggregation Toggle */}
                <Box sx={{ flex: "1 1 60%", minHeight: 0, position: "relative" }}>
                  {/* Aggregation Mode Toggle - Top left of graph card, only for expenses/incomes */}
                  {selectedTab !== "adjustments" && (
                    <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 1 }}>
                      <ToggleButtonGroup
                        value={aggregationMode}
                        exclusive
                        onChange={(_, newValue) => newValue && setAggregationMode(newValue)}
                        size="small"
                        sx={{ 
                          userSelect: "none",
                          height: "24px",
                          "& .MuiToggleButton-root": {
                            px: 1,
                            py: 0.25,
                            fontSize: "0.7rem",
                            border: 1,
                            borderColor: "divider",
                            textTransform: "none",
                            minWidth: "auto",
                            "&.Mui-selected": {
                              bgcolor: "primary.main",
                              color: "primary.contrastText",
                              "&:hover": {
                                bgcolor: "primary.dark",
                              },
                            },
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          },
                        }}
                      >
                        <ToggleButton value="group" sx={{ userSelect: "none" }}>Group</ToggleButton>
                        <ToggleButton value="category" sx={{ userSelect: "none" }}>Category</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  )}
                  <AnalyticsPieChart
                    data={pieChartData}
                    selectedTab={selectedTab}
                    isShowingPlaceholder={isShowingPlaceholder}
                    shouldHidePlaceholder={shouldHidePlaceholder}
                    shouldAnimate={shouldAnimate}
                    loading={loading}
                    formatAmount={formatAmount}
                    categoryData={categoryData}
                    hasAnimatedOnMount={hasAnimatedOnMountRef.current}
                  />
                </Box>
                <AnalyticsCategoryList
                  key={`category-list-${selectedTab}-${aggregationMode}-${fromDate}-${toDate}`}
                  selectedTab={selectedTab}
                  isShowingPlaceholder={isShowingPlaceholder}
                  categoryData={categoryData}
                  totalAmount={totalAmount}
                  formatAmount={formatAmount}
                  getCategoryColor={getCategoryColor}
                  placeholderItems={placeholderItems}
                  accounts={accounts}
                  selectedAccounts={selectedAccounts}
                  selectedUsers={selectedUsers}
                  workspaceMembersLength={workspaceMembers.length}
                  fromDate={fromDate}
                  toDate={toDate}
                  selectedPeriod={selectedPeriod}
                  onNavigateToActivity={onNavigateToActivity}
                  aggregationMode={aggregationMode}
                />
              </Box>
            </Paper>
            
            {/* Guide Section - Show when no data */}
            {filteredTransactions.length === 0 && !loading && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: 1,
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Alert
                  icon={<InfoIcon />}
                  severity="info"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    No {selectedTab} data available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedTab === "expenses" && "To see your expense analytics, go to the Activity page and add expense transactions."}
                    {selectedTab === "incomes" && "To see your income analytics, go to the Activity page and add income transactions."}
                    {selectedTab === "adjustments" && "Adjustments will appear here when you make manual changes to account balances in the Activity page."}
                  </Typography>
                </Alert>
                {(selectedTab === "expenses" || selectedTab === "incomes") && onNavigateToActivity && (
                  <Button
                    variant="contained"
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigateToActivity();
                    }}
                    endIcon={<ArrowForwardIcon />}
                    sx={{ mt: 2 }}
                  >
                    Go to Activity page to add {selectedTab}
                  </Button>
                )}
                {selectedTab === "adjustments" && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Adjustments are manual changes to an account's balance.
                  </Typography>
                )}
              </Paper>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Analytics;
